import React, { useState } from "react";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { MonthlySnapshotDashboard } from "@/components/admin/snapshot/MonthlySnapshotDashboard";
import { useMonthlySnapshot, useAllSnapshots } from "@/hooks/useMonthlySnapshot";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, BarChart3, Camera, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

const MONTH_NAMES = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", 
  "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม",
  "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"
];

export default function AdminMonthlySnapshotPage() {
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [captureMonth, setCaptureMonth] = useState(new Date().getMonth() + 1);
  const [captureYear, setCaptureYear] = useState(2026);
  const [dialogOpen, setDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  
  const { data: availableSnapshots } = useAllSnapshots();
  
  const selectedMonth = selectedPeriod ? parseInt(selectedPeriod.split("-")[0]) : undefined;
  const selectedYear = selectedPeriod ? parseInt(selectedPeriod.split("-")[1]) : undefined;
  
  const { data, isLoading, error } = useMonthlySnapshot(selectedMonth, selectedYear);

  const handlePeriodChange = (value: string) => {
    setSelectedPeriod(value === "latest" ? null : value);
  };

  const handleCaptureSnapshot = async () => {
    setIsCapturing(true);
    try {
      const { data, error } = await supabase.functions.invoke('capture-monthly-snapshot', {
        body: { month: captureMonth, year: captureYear }
      });
      
      if (error) throw error;
      
      toast({
        title: "บันทึกสำเร็จ",
        description: `บันทึก Snapshot ${MONTH_NAMES[captureMonth - 1]} ${captureYear} เรียบร้อย`,
      });
      
      // Refresh data
      queryClient.invalidateQueries({ queryKey: ["monthly-snapshot"] });
      queryClient.invalidateQueries({ queryKey: ["all-monthly-snapshots"] });
      setDialogOpen(false);
    } catch (error: any) {
      toast({
        title: "เกิดข้อผิดพลาด",
        description: error.message || "ไม่สามารถบันทึก Snapshot ได้",
        variant: "destructive",
      });
    } finally {
      setIsCapturing(false);
    }
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AdminSidebar />
        <SidebarInset className="flex-1">
          <div className="container py-6 px-4 md:px-6 lg:px-8 max-w-7xl">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <BarChart3 className="h-6 w-6" />
                  สรุปข้อมูลรายเดือน
                </h1>
                <p className="text-muted-foreground mt-1">
                  ภาพรวมผลดำเนินงาน Fishsix พร้อมเปรียบเทียบกับเดือนก่อน
                </p>
              </div>
              
              <div className="flex gap-2">
                <Select
                  value={selectedPeriod || "latest"}
                  onValueChange={handlePeriodChange}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="เลือกเดือน" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">ล่าสุด</SelectItem>
                    {availableSnapshots?.map((snap) => (
                      <SelectItem 
                        key={`${snap.month}-${snap.year}`} 
                        value={`${snap.month}-${snap.year}`}
                      >
                        {MONTH_NAMES[snap.month - 1]} {snap.year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <Camera className="h-4 w-4" />
                      บันทึก Snapshot
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>บันทึก Monthly Snapshot</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="grid gap-2">
                        <label className="text-sm font-medium">เดือน</label>
                        <Select value={captureMonth.toString()} onValueChange={(v) => setCaptureMonth(parseInt(v))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {MONTH_NAMES.map((name, i) => (
                              <SelectItem key={i+1} value={(i+1).toString()}>{name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-2">
                        <label className="text-sm font-medium">ปี</label>
                        <Select value={captureYear.toString()} onValueChange={(v) => setCaptureYear(parseInt(v))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2025">2025</SelectItem>
                            <SelectItem value="2026">2026</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={handleCaptureSnapshot} disabled={isCapturing}>
                        {isCapturing ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            กำลังบันทึก...
                          </>
                        ) : (
                          "บันทึก Snapshot"
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Current Period Display */}
            {data?.current && (
              <div className="mb-6 p-4 rounded-lg bg-primary/5 border">
                <p className="text-lg font-medium">
                  📅 {MONTH_NAMES[data.current.month - 1]} {data.current.year}
                </p>
                {data.previous && (
                  <p className="text-sm text-muted-foreground">
                    เปรียบเทียบกับ {MONTH_NAMES[data.previous.month - 1]} {data.previous.year}
                  </p>
                )}
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} className="h-32" />
                  ))}
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-32" />
                  ))}
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  เกิดข้อผิดพลาดในการโหลดข้อมูล: {error.message}
                </AlertDescription>
              </Alert>
            )}

            {/* No Data State */}
            {!isLoading && !error && !data?.current && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  ไม่พบข้อมูลสำหรับเดือนที่เลือก
                </AlertDescription>
              </Alert>
            )}

            {/* Dashboard Content */}
            {data?.current && (
              <MonthlySnapshotDashboard
                current={data.current}
                percentChanges={data.percentChanges}
              />
            )}
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}
